import { Comment, SeempAction, SeempReport } from "api"
import { portalSocket } from "./portalSocket"

class SeempSocket {
	private onLockCallbacks: ((data: {
		reportId: number
		actionId: number
		userEmail: string
		userName: string
	}) => void)[] = []
	private onUnlockCallbacks: ((data: { reportId: number; actionId: number }) => void)[] = []
	private onActionsLockedStateUpdateCallbacks: ((
		data: {
			reportId: number
			actions: { actionId: number; userEmail: string; userName: string }[]
		}[]
	) => void)[] = []
	private onPlantCacheInvalidatedCallbacks: ((data: { plantId: number }) => void)[] = []
	private onPlantCacheUpdatedCallbacks: ((data: {
		plantId: number
		updateType: "add" | "update" | "delete"
		data?: SeempAction | SeempReport | Comment
		reportId: number
		actionId?: number
		commentId?: number
	}) => void)[] = []

	constructor() {
		portalSocket.addMessageListener(
			"seempActionLocked",
			({ reportId, actionId, userEmail, userName }) => {
				this.onLockCallbacks.forEach((cb) =>
					cb({ reportId, actionId, userEmail, userName })
				)
			}
		)
		portalSocket.addMessageListener("seempActionUnlocked", ({ reportId, actionId }) => {
			this.onUnlockCallbacks.forEach((cb) => cb({ reportId, actionId }))
		})
		portalSocket.addMessageListener("seempActionsLockedState", (data) => {
			this.onActionsLockedStateUpdateCallbacks.forEach((cb) => cb(data))
		})
		portalSocket.addMessageListener("seempPlantCacheInvalidated", (data) => {
			this.onPlantCacheInvalidatedCallbacks.forEach((cb) => cb(data))
		})
		portalSocket.addMessageListener("seempPlantCacheUpdated", (data) => {
			this.onPlantCacheUpdatedCallbacks.forEach((cb) => cb(data))
		})
	}

	onLock(
		cb: (data: {
			reportId: number
			actionId: number
			userEmail: string
			userName: string
		}) => void
	) {
		this.onLockCallbacks[0] = cb
	}
	offLock(
		cb: (data: {
			reportId: number
			actionId: number
			userEmail: string
			userName: string
		}) => void
	) {
		this.onLockCallbacks = this.onLockCallbacks.filter((fn) => fn !== cb)
	}

	onUnlock(cb: (data: { reportId: number; actionId: number }) => void) {
		this.onUnlockCallbacks[0] = cb
	}
	offUnlock(cb: (data: { reportId: number; actionId: number }) => void) {
		this.onUnlockCallbacks = this.onUnlockCallbacks.filter((fn) => fn !== cb)
	}

	onPlantCacheInvalidated(cb: (data: { plantId: number }) => void) {
		this.onPlantCacheInvalidatedCallbacks[0] = cb
	}
	offPlantCacheInvalidated(cb: (data: { plantId: number }) => void) {
		this.onPlantCacheInvalidatedCallbacks = this.onPlantCacheInvalidatedCallbacks.filter(
			(fn) => fn !== cb
		)
	}

	onPlantCacheUpdated(
		cb: (data: {
			plantId: number
			updateType: "add" | "update" | "delete"
			data?: SeempAction | SeempReport | Comment
			reportId: number
			actionId?: number
			commentId?: number
		}) => void
	) {
		this.onPlantCacheUpdatedCallbacks[0] = cb
	}
	offPlantCacheUpdated(
		cb: (data: {
			plantId: number
			updateType: "add" | "update" | "delete"
			data?: SeempAction | SeempReport | Comment
			reportId: number
			actionId?: number
			commentId?: number
		}) => void
	) {
		this.onPlantCacheUpdatedCallbacks = this.onPlantCacheUpdatedCallbacks.filter(
			(fn) => fn !== cb
		)
	}

	onActionsLockedStateUpdate(
		cb: (
			data: {
				reportId: number
				actions: { actionId: number; userEmail: string; userName: string }[]
			}[]
		) => void
	) {
		this.onActionsLockedStateUpdateCallbacks[0] = cb
	}
	offActionsLockedStateUpdate(
		cb: (
			data: {
				reportId: number
				actions: { actionId: number; userEmail: string; userName: string }[]
			}[]
		) => void
	) {
		this.onActionsLockedStateUpdateCallbacks = this.onActionsLockedStateUpdateCallbacks.filter(
			(fn) => fn !== cb
		)
	}

	joinSeempCompanyRoom(companyName: string) {
		portalSocket.sendMessage("joinSeempCompanyRoom", { companyName })
	}

	joinSeempPlantRoom(companyName: string, plantId: number) {
		if (portalSocket.socket?.id === undefined) {
			return
		}
		portalSocket.sendMessage("joinSeempPlantRoom", {
			companyName,
			plantId,
			socketId: portalSocket.socket.id,
		})
	}

	leaveSeempPlantRoom(companyName: string, plantId: number, userEmail: string) {
		portalSocket.sendMessage("leaveSeempPlantRoom", { companyName, plantId, userEmail })
	}

	getLockedActions(companyName: string, plantId: number) {
		if (portalSocket.socket?.id === undefined) {
			return
		}
		portalSocket.sendMessage("getLockedActions", {
			companyName,
			plantId,
			socketId: portalSocket.socket.id,
		})
	}

	leaveSeempReport(companyName: string, plantId: number, reportId: number, userEmail: string) {
		portalSocket.sendMessage("leaveSeempReport", {
			companyName,
			plantId,
			reportId,
			userEmail,
		})
	}

	lockReportAction(
		companyName: string,
		plantId: number,
		reportId: number,
		actionId: number,
		user: {
			email: string
			name: string
		}
	) {
		portalSocket.sendMessage("lockSeempAction", {
			companyName,
			plantId,
			reportId,
			actionId,
			user,
		})
	}

	sendInvalidatePlantCacheRequest(companyName: string, plantId: number) {
		portalSocket.sendMessage("invalidateSeempPlantCache", { companyName, plantId })
	}

	sendPlantCacheUpdateRequest(
		companyName: string,
		plantId: number,
		updateType: "add" | "update" | "delete",
		data: undefined | SeempAction | SeempReport | Comment,
		reportId: number,
		actionId?: number,
		commentId?: number
	) {
		portalSocket.sendMessage("updateSeempPlantCache", {
			companyName,
			plantId,
			updateType,
			data,
			reportId,
			actionId,
			commentId,
		})
	}

	unlockReportAction(
		companyName: string,
		plantId: number,
		reportId: number,
		actionId: number,
		userEmail: string
	) {
		portalSocket.sendMessage("unlockSeempAction", {
			companyName,
			plantId,
			reportId,
			actionId,
			userEmail,
		})
	}
}

export const seempSocket = new SeempSocket()
